// Загрузка файлов к комментарию: presign → PUT в хранилище → complete.
// Та же схема, что у голосовых (см. handleSubmit в WriteComment), но с типом IMAGE / FILE.
import { completeMediaUpload, createMediaPresign } from "../../store/projectsSlice";

export const ATTACH_LIMITS = {
  maxFiles: 10,
  imageBytes: 20 * 1024 * 1024, // как media.max-size-bytes.image на сервере
  fileBytes: 10 * 1024 * 1024, // как media.max-size-bytes.file
};

export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
export const FILE_TYPES = [
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/rtf",
  "application/rtf",
  "application/x-subrip",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

// Браузер не всегда знает MIME-тип (.md, .srt, .txt на Windows) — подбираем по расширению
const EXT_TO_TYPE = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  rtf: "application/rtf",
  srt: "application/x-subrip",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export const ACCEPT_ATTR = [
  ...IMAGE_TYPES,
  ...FILE_TYPES,
  ".txt",
  ".md",
  ".csv",
  ".rtf",
  ".srt",
  ".pdf",
  ".doc",
  ".docx",
].join(",");

export function fileExtension(name) {
  const m = /\.([a-z0-9]+)$/i.exec(name || "");
  return m ? m[1].toLowerCase() : "";
}

/**
 * Определяет, можно ли приложить файл, и каким типом.
 * Возвращает { kind: "image" | "file", contentType } или { error }.
 */
export function classifyFile(file) {
  const ext = fileExtension(file.name);
  let contentType = (file.type || "").split(";")[0].trim();
  if (!contentType || contentType === "application/octet-stream") {
    contentType = EXT_TO_TYPE[ext] || "";
  }
  if (contentType === "text/x-markdown") contentType = "text/markdown";
  if (contentType === "text/srt" || contentType === "application/srt") {
    contentType = "application/x-subrip";
  }
  if (IMAGE_TYPES.includes(contentType)) {
    if (file.size > ATTACH_LIMITS.imageBytes) {
      return { error: `Картинка больше ${formatBytes(ATTACH_LIMITS.imageBytes)}: ${file.name}` };
    }
    return { kind: "image", contentType };
  }
  if (FILE_TYPES.includes(contentType)) {
    if (file.size > ATTACH_LIMITS.fileBytes) {
      return { error: `Файл больше ${formatBytes(ATTACH_LIMITS.fileBytes)}: ${file.name}` };
    }
    return { kind: "file", contentType };
  }
  return { error: `Такой тип файла не поддерживается: ${file.name}` };
}

export function formatBytes(n) {
  if (!n && n !== 0) return "";
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, "")} МБ`;
  if (n >= 1024) return `${Math.round(n / 1024)} КБ`;
  return `${n} Б`;
}

/**
 * Загружает один файл. Возвращает mediaId. Бросает Error с понятным текстом.
 */
export async function uploadAttachment(dispatch, { file, kind, contentType, versionId }) {
  const presignRes = await dispatch(
    createMediaPresign({
      mediaData: {
        type: kind === "image" ? "IMAGE" : "FILE",
        contentType,
        contextKind: "VIDEO",
        contextId: +versionId,
        fileName: file.name,
      },
    })
  );
  if (presignRes.error) {
    throw new Error(presignMessage(presignRes.payload) || `Не удалось начать загрузку: ${file.name}`);
  }
  const { uploadUrl, requiredHeaders, mediaId } = presignRes.payload;
  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: requiredHeaders,
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Не удалось загрузить файл: ${file.name}`);
  }
  const completeRes = await dispatch(completeMediaUpload({ mediaId, mediaData: {} }));
  if (completeRes.error) {
    throw new Error(presignMessage(completeRes.payload) || `Файл не принят сервером: ${file.name}`);
  }
  return mediaId;
}

function presignMessage(payload) {
  if (!payload) return "";
  if (typeof payload === "string") return payload;
  return payload.message || payload.error || "";
}
